import { useState } from 'react';
import { useSelector } from 'react-redux';
import { getIsJourneysHoldCustomTimeEnabled } from 'modules/workflows/ducks';
import { HoldNodeData } from 'modules/workflows/types';
import {
  defaultErrorState,
  Duration,
} from 'modules/workflowNodeEditors/components/formElements/DurationForm';
import { ErrorText } from 'ui/formField';
import noop from 'utils/noop';
import {
  CUSTOM_DURATION_VALUE,
  durationOptions,
  durationOptionsWithCustom,
  durationFormToExpireDataMap,
  MAX_DAYS,
} from './HoldDurationSection.constants';
import {
  DurationFieldsContainer,
  StyledDurationForm,
  WideSelect,
} from './HoldDurationSection.styles';
import { getDefaultDurationValues, validateDuration } from './HoldDurationSection.utils';
import { StyledHeader, StyledTitle } from '../HoldEditor.styled';

interface HoldDurationSectionProps {
  isReadOnly?: boolean;
  /** callback when duration has changed */
  onChange?: (data: Partial<HoldNodeData>) => void;
  /** nodeData for the hold node */
  value?: Partial<HoldNodeData>;
  [key: string]: any;
}

const HoldDurationSection = ({
  onChange = noop,
  value = {},
  isReadOnly = false,
  setErrorState = noop,
  errorState,
  hasError,
}: HoldDurationSectionProps) => {
  const isJourneysHoldCustomTimeEnabled = useSelector(getIsJourneysHoldCustomTimeEnabled);
  const { expireHours, expireDays, expireMinutes } = value;
  const [selectedValues, setSelectedValues] = useState<string[]>(
    getDefaultDurationValues(
      { expireHours, expireDays, expireMinutes },
      isJourneysHoldCustomTimeEnabled,
    ),
  );

  const handleUpdateDurationFields = (valueName: Duration, inputValue: number) => {
    const expireDataKey = durationFormToExpireDataMap[valueName];
    const updatedValue = { ...value, [expireDataKey]: inputValue };
    const areFieldsValid = validateDuration(updatedValue);

    setErrorState({
      ...defaultErrorState,
      [valueName]: !areFieldsValid,
    });

    onChange(updatedValue);
  };

  return (
    <>
      <StyledTitle>Hold duration</StyledTitle>
      <StyledHeader>
        Users who don’t meet your criteria by the end of this period will go down the “Rules not
        met” branch.
      </StyledHeader>
      <DurationFieldsContainer>
        <WideSelect
          defaultValues={selectedValues}
          options={isJourneysHoldCustomTimeEnabled ? durationOptionsWithCustom : durationOptions}
          placeholder="Select a duration"
          onSelect={(dropdownValues, { selectedOptions }) => {
            // Set dropdown value used only by this component
            setSelectedValues(dropdownValues as string[]);
            // Set days/hours/minutes in the node data
            onChange((selectedOptions?.[0]?.data || {}) as Partial<HoldNodeData>);
          }}
          id="hold-duration-select"
          isDisabled={isReadOnly}
          isFullWidth
        />
        {isJourneysHoldCustomTimeEnabled && selectedValues[0] === CUSTOM_DURATION_VALUE && (
          <StyledDurationForm
            maxDays={MAX_DAYS}
            isReadOnly={isReadOnly}
            onUpdateField={handleUpdateDurationFields}
            errorState={errorState}
            values={{
              [Duration.Day]: expireDays || undefined,
              [Duration.Hour]: expireHours || undefined,
              [Duration.Minute]: expireMinutes || undefined,
            }}
          />
        )}
      </DurationFieldsContainer>
      {hasError && <ErrorText>Duration should not exceed {MAX_DAYS} days.</ErrorText>}
    </>
  );
};

export default HoldDurationSection;
